/**
 * 日本の祝日(カレンダーの色分け用。2026-09-10 指示: 土曜は青、日曜・祝日は赤)。
 * 1980〜2099年に対応。春分・秋分は天文計算の近似式(この期間は実際の官報と一致する)。
 * 振替休日(日曜が祝日のとき次の平日)と国民の休日(祝日に挟まれた平日)も含む。
 */
import type { DateStr } from './types';

const pad = (n: number) => String(n).padStart(2, '0');
const key = (y: number, m: number, d: number): DateStr => `${y}-${pad(m)}-${pad(d)}`;
/** その月の n 回目の曜日(0=日)の日 */
function nthDow(y: number, m: number, dow: number, n: number): number {
  const first = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
  return 1 + ((dow - first + 7) % 7) + (n - 1) * 7;
}
const vernal = (y: number) => Math.floor(20.8431 + 0.242194 * (y - 1980) - Math.floor((y - 1980) / 4));
const autumnal = (y: number) => Math.floor(23.2488 + 0.242194 * (y - 1980) - Math.floor((y - 1980) / 4));

const cache = new Map<number, Map<DateStr, string>>();

/** その年の祝日(日付 → 名前) */
export function holidaysOf(year: number): Map<DateStr, string> {
  const hit = cache.get(year);
  if (hit) return hit;
  const base = new Map<DateStr, string>();
  const add = (m: number, d: number, name: string) => base.set(key(year, m, d), name);
  add(1, 1, '元日');
  add(1, nthDow(year, 1, 1, 2), '成人の日');
  add(2, 11, '建国記念の日');
  if (year >= 2020) add(2, 23, '天皇誕生日');
  add(3, vernal(year), '春分の日');
  add(4, 29, '昭和の日');
  add(5, 3, '憲法記念日');
  add(5, 4, 'みどりの日');
  add(5, 5, 'こどもの日');
  add(7, nthDow(year, 7, 1, 3), '海の日');
  add(8, 11, '山の日');
  add(9, nthDow(year, 9, 1, 3), '敬老の日');
  add(9, autumnal(year), '秋分の日');
  add(10, nthDow(year, 10, 1, 2), 'スポーツの日');
  add(11, 3, '文化の日');
  add(11, 23, '勤労感謝の日');

  const out = new Map(base);
  // 振替休日: 日曜が祝日なら、次の祝日でない日
  for (const [d, name] of base) {
    const dt = new Date(`${d}T00:00:00Z`);
    if (dt.getUTCDay() !== 0) continue;
    const next = new Date(dt);
    do {
      next.setUTCDate(next.getUTCDate() + 1);
    } while (out.has(key(next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate())));
    out.set(key(next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate()), `振替休日(${name})`);
  }
  // 国民の休日: 祝日に挟まれた平日(敬老の日と秋分の日の間など)
  for (const [d] of base) {
    const dt = new Date(`${d}T00:00:00Z`);
    const mid = new Date(dt);
    mid.setUTCDate(mid.getUTCDate() + 1);
    const after = new Date(dt);
    after.setUTCDate(after.getUTCDate() + 2);
    const midKey = key(mid.getUTCFullYear(), mid.getUTCMonth() + 1, mid.getUTCDate());
    const afterKey = key(after.getUTCFullYear(), after.getUTCMonth() + 1, after.getUTCDate());
    if (base.has(afterKey) && !out.has(midKey) && mid.getUTCDay() !== 0) out.set(midKey, '国民の休日');
  }
  cache.set(year, out);
  return out;
}

/** 祝日なら名前、そうでなければ null */
export function holidayName(date: DateStr): string | null {
  return holidaysOf(Number(date.slice(0, 4))).get(date) ?? null;
}

/** カレンダーの色分け: 日曜・祝日 = 赤 / 土曜 = 青 / それ以外 = null */
export function dayColorOf(date: DateStr): 'red' | 'blue' | null {
  const dow = new Date(`${date}T00:00:00Z`).getUTCDay();
  if (dow === 0 || holidayName(date)) return 'red';
  if (dow === 6) return 'blue';
  return null;
}

/** 日付の文字色のクラス(土=青・日祝=赤。システム全体で同じ。2026-09-20 発注者指示) */
export function dayTextClass(date: DateStr): string {
  const c = dayColorOf(date);
  return c === 'red' ? 'day-sun' : c === 'blue' ? 'day-sat' : '';
}
